import { useCallback, useMemo } from "react";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";

import { defaultToastOptions } from "../../../../utils/misc";
import { MemberDataType, StripeBillingDataType, MatchState } from "../../types";
import {
  usePostCreateSingleBillingRecord,
  usePostRejectPaymentMethod,
  useUpdateStripeId,
} from "../../api/migrationAssistantApi.hooks";
import { createConsolidatedColumns } from "./data-table/consolidated-columns";
import { DataTable } from "./data-table/data-table-payment-matching";
import { getErrorMessage } from "../../../../utils/errorHandling";
import LoadingOverlay from "./LoadingOverlay";
import { useExperiment } from "../../../../hooks/useExperiment";
import { SHOW_MEMBER_PROFILE_FEATURE_FLAG } from "../../../../context/features/features.utils";

export default function ConsolidatedTabView({
  matchedMembers,
  noMatchMembers,
  stripeBillingData,
  isFetchingStripeBillingData,
  setSelectedMemberId,
  setShowMultiPaymentsDrawer,
}: {
  matchedMembers: MemberDataType[];
  noMatchMembers: MemberDataType[];
  stripeBillingData: StripeBillingDataType["data"];
  isFetchingStripeBillingData: boolean;
  setSelectedMemberId: (value: number) => void;
  setShowMultiPaymentsDrawer: (value: boolean) => void;
}) {
  const { useExperimentValue } = useExperiment();
  const isMemberProfilePageEnabled = useExperimentValue(
    SHOW_MEMBER_PROFILE_FEATURE_FLAG,
    true,
  ) as boolean;

  const queryClient = useQueryClient();
  // eslint-disable-next-line
  const { mutate: rejectPaymentMethod, isLoading: rejectPaymentMethodLoading } =
    usePostRejectPaymentMethod();

  const { mutate: createSingleBilling, isLoading: createSingleBillingLoading } =
    usePostCreateSingleBillingRecord();

  const { mutate: updateStripeId } = useUpdateStripeId();

  const combinedMembers = useMemo(() => {
    const eligibleMembers = [...matchedMembers, ...noMatchMembers].filter(
      (m) => m.userUuid && !m.parentEmail,
    );
    const membersWithStatus = eligibleMembers.map((member) => ({
      ...member,
      matchState: member.matchState ?? MatchState.UNMATCHED,
    }));
    return membersWithStatus as MemberDataType[];
  }, [matchedMembers, noMatchMembers]);

  const handleRejectPaymentMethod = useCallback(
    (userId: number) => {
      rejectPaymentMethod(userId, {
        onSuccess: () => {
          toast.success("Payment method rejected", defaultToastOptions);
          queryClient.invalidateQueries({
            queryKey: ["fetchMigratingMembers"],
          });
        },
        onError: () => {
          toast.error(
            "There was an error rejecting payment method",
            defaultToastOptions,
          );
        },
      });
    },
    [rejectPaymentMethod, queryClient],
  );

  const handleAcceptSinglePaymentMethod = useCallback(
    (member: MemberDataType) => {
      if (!member.stripeId) {
        toast.error(
          "No Stripe ID found. Please check the Stripe ID and try again.",
          defaultToastOptions,
        );
        return;
      }

      if (!stripeBillingData[member.stripeId]) {
        toast.error(
          "No Stripe data found for this member. Please check the Stripe ID and try again.",
          defaultToastOptions,
        );
        return;
      }

      if (stripeBillingData[member.stripeId].paymentMethods.data.length === 0) {
        toast.error(
          "No payment method found. Please check the Stripe ID and try again.",
          defaultToastOptions,
        );
        return;
      }

      const billingReference: string =
        stripeBillingData[member.stripeId]?.paymentMethods.data[0].id;

      createSingleBilling(
        {
          userId: member.id,
          userUuid: member.userUuid,
          paymentReference: billingReference,
          customerReference: member.stripeId,
          isPermanent: true,
        },
        {
          onSuccess: () => {
            toast.success("Payment method accepted", defaultToastOptions);
            queryClient.invalidateQueries({
              queryKey: ["fetchMigratingMembers"],
            });
          },
          onError: (error) => {
            const errorMessage = `${getErrorMessage(error, "There was an error accepting payment method")}`;
            toast.error(errorMessage, defaultToastOptions);
          },
        },
      );
    },
    [createSingleBilling, queryClient, stripeBillingData],
  );

  const handleUpdateStripeId = useCallback(
    (data: { userId: number; stripeId: string }) => {
      updateStripeId(data, {
        onSuccess: () => {
          toast.success("User updated successfully", defaultToastOptions);
          queryClient.invalidateQueries({
            queryKey: ["fetchMigratingMembers"],
          });
          queryClient.invalidateQueries({
            queryKey: ["fetchStripeBillingData"],
          });
        },
        onError: (error) => {
          const errorMessage = getErrorMessage(
            error,
            "Failed to update user. Please check the Stripe ID and try again.",
          );
          queryClient.invalidateQueries({
            queryKey: ["fetchMigratingMembers"],
          });
          toast.error(errorMessage, defaultToastOptions);
        },
      });
    },
    [updateStripeId, queryClient],
  );

  const columns = useMemo(
    () =>
      createConsolidatedColumns({
        stripeBillingData,
        setSelectedMemberId,
        setShowMultiPaymentsDrawer,
        handleRejectPaymentMethod,
        handleAcceptSinglePaymentMethod,
        handleUpdateStripeId,
        isFetchingStripeBillingData,
        createSingleBillingLoading,
        isMemberProfilePageEnabled,
      }),
    [
      stripeBillingData,
      setSelectedMemberId,
      handleRejectPaymentMethod,
      setShowMultiPaymentsDrawer,
      handleAcceptSinglePaymentMethod,
      handleUpdateStripeId,
      isFetchingStripeBillingData,
      createSingleBillingLoading,
      isMemberProfilePageEnabled,
    ],
  );

  return (
    <div className="tw-overflow-x-auto">
      {
        <div className="tw-relative">
          <DataTable columns={columns} data={combinedMembers || []} />
          {isFetchingStripeBillingData && <LoadingOverlay />}
        </div>
      }
    </div>
  );
}
